import React, { createContext, useContext, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { loadingStatus } from "core/store/actions";
import { clearUser } from "core/store/user/actions";
import api from "core/services/api";
import { t } from "i18next";

const CommonContext = createContext();

const dbAuth = {
  type: {
    A: {
      label: "Admin",
      value: "A",
      key: "A",
    },
    G: {
      label: "Agent",
      value: "G",
      key: "G",
    },
    D: {
      label: "User Agent",
      value: "D",
      key: "D",
    },
    U: {
      label: "User",
      value: "U",
      key: "U",
    },
  },
}

const hasPermission = (type) => {
  const envProfileType = process.env.REACT_APP_TYPE?.trim();
  switch (envProfileType) {
    case "USER":
      return type === dbAuth.type.U.value || type === dbAuth.type.D.value;
    case "ALL":
      return type === dbAuth.type.A.value || type === dbAuth.type.G.value || type === dbAuth.type.D.value || type === dbAuth.type.U.value;
    default:
      return type === dbAuth.type.A.value || type === dbAuth.type.G.value;
  }
};

export const CommonProvider = ({ children }) => {
  const [error, setError] = useState();
  const [userType, setUserType] = useState();

  const [signUp, setSignUp] = useState(null);
  const [loading, setLoading] = useState(false);
  const [singupWaitLimit, setSignupWaitLimit] = useState(false);

  const [userState, setUserState] = useState(""); //2024.10.16 Redux 사용이후 사용 X??? 
  const [isRefer, setIsRefer] = useState(0);

  const [inputError, setInputError] = useState({});
  const [eventSource, setEventSource] = useState({});

  /**
   * API 에러 응답을 처리하여 inputError 상태를 업데이트하는 함수
   * @param {Array} errors - API에서 받은 에러 배열
   * @param {boolean} isInit - 초기화 여부 (기본값: false)
   */
  const handleApiErrors = (errors = [], isInit = false) => {
    if (errors.length == 0 || !Array.isArray(errors)) return;
    if (isInit) {
      // 초기화 시에는 전체 에러 상태를 새로 설정
      const errorObj = {};
      errors.forEach(error => {
        errorObj[error.path] = translateErrorMessage(error);
      });
      setInputError(errorObj);
    } else {
      // 기존 에러 상태에 새로운 에러들을 병합
      setInputError((prev) => {
        const newErrors = { ...prev };
        
        errors.forEach(error => {
          newErrors[error.path] = translateErrorMessage(error);
        });

        return newErrors;
      });
    }
  };

  /**
   * 단일 에러 객체를 번역된 메시지로 변환
   * @param {Object} error - API 에러 객체
   * @returns {string} 번역된 에러 메시지
   */
  const translateErrorMessage = (error) => {
    const msgCode = error.msg.code;
    const params = error.msg.params || [];
    const values = error.msg.values || [];
    
    // 번역 옵션 객체 생성
    const translationOptions = {};
    
    // param 값 설정 (필드명을 한국어로 변환)
    if (params.length > 0) {
      translationOptions.param = t(`fieldName.${params[0]}`) || params[0];
    } else {
      // params가 없는 경우 error.path를 사용
      translationOptions.param = t(`fieldName.${error.path}`) || error.path;
    }
    
    // value0, value1 등의 값 설정
    values.forEach((value, index) => {
      translationOptions[`value${index}`] = value;
    });
    
    // 번역 메시지 가져오기
    let translatedMessage = t(`error2.${msgCode}`);
    
    // 수동으로 파라미터 치환 (i18next interpolation이 작동하지 않는 경우 대비)
    if (translationOptions.param) {
      translatedMessage = translatedMessage.replace(/\{\{param\}\}/g, translationOptions.param);
    }
    
    // value0, value1 등 치환
    Object.keys(translationOptions).forEach(key => {
      if (key.startsWith('value')) {
        const regex = new RegExp(`\\{\\{${key}\\}\\}`, 'g');
        translatedMessage = translatedMessage.replace(regex, translationOptions[key]);
      }
    });
    
    return translatedMessage || t("error2.unknown");
  };

  const dispatch = useDispatch();
  const user = useSelector((s) => s.User) || {};
  const isLogin = !!user.id && hasPermission(user.type);
  const setIsLogin = (val) => { if (!val) dispatch(clearUser()); };

  const [readNote, setReadNote] = useState({});

  const [siteName, setSiteName] = useState("");
  const [siteOpenType, setSiteOpenType] = useState(null); // 사이트 오픈 타입 (1: 오픈형, 2: 일반, 3: 폐쇄형)
  const getUser = async () => {
    setLoading(true);
    dispatch(loadingStatus(true));
    try {
      const res = await api.get("/user");
      setUserState(res.data.content.state1);
      setIsRefer(res.data.content.isRefer);
      setSignUp(res.data.content.is_exist_user_data);
      setUserType(res.data.content.type);
      setSignupWaitLimit(res.data.content.signup_wait_limit)
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
      dispatch(loadingStatus(false));
    }
  };


  function setTitle(siteName) {
    document.title = siteName;
  }

  function initSiteName(siteName) {
    document.title = siteName;
    setSiteName(document.title);
  }
  const contextValue = {
    getUser,

    loading,
    setLoading,

    isLogin,
    setIsLogin,

    inputError,
    setInputError,
    handleApiErrors,

    error,
    setError,

    isRefer,
    setIsRefer,

    singupWaitLimit,
    setSignupWaitLimit,

    eventSource,
    setEventSource,

    signUp,
    userState,

    readNote,
    setReadNote,

    siteName,
    initSiteName,
    setTitle,

    userType,

    siteOpenType,
    setSiteOpenType,
  };

  return <CommonContext.Provider value={contextValue}>{children}</CommonContext.Provider>;
};

export const useCommonState = () => {
  const context = useContext(CommonContext);
  if (!context) {
    throw new Error("useCommonContext must be used within a CommonProvider");
  }
  return context;
};
